import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  getReconciliation,
  getReconciliationCategory,
} from "../services/xeroBillsService";

/**
 * MyGlobe and Xero compared — P2P v1.
 *
 * Rendered as the "Comparison" tab of the Xero bills screen, not as a page of its own.
 * Bills that exist in Xero with nothing here are not shown: those are the "To record" tab,
 * and counting them in both places produced two different numbers for the same thing.
 *
 * Read-only. Shows where the two systems disagree; nothing is corrected here. Today nothing
 * surfaces these differences at all, so the first run is as much a measure of the gap as an
 * ongoing check. Once information flows automatically it becomes a health check: three
 * zeroes means the two systems are in step.
 */

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-GB") : "—");

const fmtMoney = (v) =>
  v === null || v === undefined || v === ""
    ? "—"
    : new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(Number(v));

const PAGE_SIZE = 50;

const XeroReconciliation = () => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const [openKey, setOpenKey] = useState(null);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [page, setPage] = useState(1);
  const [rowsLoading, setRowsLoading] = useState(false);

  const loadSummary = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getReconciliation();
      if (res.success) setSummary(res.data);
      else toast.error(res.message || "Could not build the comparison");
    } catch (e) {
      toast.error(e.message || "Could not build the comparison");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const loadRows = useCallback(async () => {
    if (!openKey) return;
    setRowsLoading(true);
    try {
      const res = await getReconciliationCategory(openKey, { page, limit: PAGE_SIZE });
      if (res.success) {
        setRows(res.data || []);
        setPagination(res.pagination || { page: 1, pages: 1, total: 0 });
      } else {
        toast.error(res.message || "Could not load those rows");
      }
    } catch (e) {
      toast.error(e.message || "Could not load those rows");
    } finally {
      setRowsLoading(false);
    }
  }, [openKey, page]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const open = (key) => {
    setOpenKey(openKey === key ? null : key);
    setPage(1);
    setRows([]);
  };

  const openCategory = summary?.categories?.find((c) => c.key === openKey);
  const allClear = summary?.categories?.every((c) => c.count === 0);

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <p className="text-muted mb-0">
          Where the two systems disagree. Nothing is changed here.
        </p>
        <button className="btn btn-outline-secondary" onClick={loadSummary} disabled={loading}>
          {loading ? "Loading…" : "Refresh"}
        </button>
      </div>

      {loading && <div className="text-muted">Building the comparison…</div>}

      {/* An empty cache is not "all clear" — it means nothing has been read from Xero yet. */}
      {!loading && summary && !summary.window && (
        <div className="alert alert-warning">
          No bills have been read from Xero yet, so there is nothing to compare. Run the sync
          first.
        </div>
      )}

      {!loading && summary?.window && (
        <>
          <p className="text-muted">
            Comparing {fmtDate(summary.window.from)} to {fmtDate(summary.window.to)} —{" "}
            {summary.window.bills.toLocaleString("en-GB")} bills in Xero.{" "}
            <span title="MyGlobe holds expenditure going back further than Xero does, so the comparison is limited to the period Xero covers">
              Why this period?
            </span>
          </p>

          {allClear && (
            <div className="alert alert-success">
              The two systems agree on everything in this period.
            </div>
          )}

          <div className="row">
            {summary.categories.map((c) => (
              <div className="col-md-4 mb-3" key={c.key}>
                <div
                  className={`card h-100 ${openKey === c.key ? "border-primary" : ""} ${
                    c.count === 0 ? "opacity-75" : ""
                  }`}
                  role="button"
                  onClick={() => c.count > 0 && open(c.key)}
                >
                  <div className="card-body">
                    <h6 className="card-title mb-1">{c.label}</h6>
                    <div className="d-flex align-items-baseline">
                      <span className={`h3 mb-0 me-2 ${c.count === 0 ? "text-success" : ""}`}>
                        {c.count.toLocaleString("en-GB")}
                      </span>
                      {c.count > 0 && (
                        <span className="text-muted">{fmtMoney(c.value)}</span>
                      )}
                    </div>
                    <small className="text-muted d-block mt-2">{c.description}</small>
                    {c.count > 0 && (
                      <small className="d-block mt-2 fst-italic text-muted">{c.action}</small>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {openKey && openCategory && (
        <div className="card mt-3">
          <div className="card-header d-flex justify-content-between align-items-center">
            <strong>{openCategory.label}</strong>
            <button className="btn btn-sm btn-link" onClick={() => setOpenKey(null)}>
              Close
            </button>
          </div>
          <div className="table-responsive">
            <table className="table table-sm table-hover mb-0">
              <thead>
                <tr>
                  <th>Supplier</th>
                  <th>Invoice no.</th>
                  <th>Date</th>
                  <th className="text-end">Xero</th>
                  <th className="text-end">MyGlobe</th>
                </tr>
              </thead>
              <tbody>
                {rowsLoading && (
                  <tr>
                    <td colSpan={5} className="text-center text-muted py-3">
                      Loading…
                    </td>
                  </tr>
                )}
                {!rowsLoading && rows.length === 0 && (
                  <tr>
                    <td colSpan={5} className="text-center text-muted py-3">
                      Nothing to show.
                    </td>
                  </tr>
                )}
                {!rowsLoading &&
                  rows.map((r, i) => (
                    <tr key={`${r.xero_invoice_id || r.expenditure_id || i}`}>
                      <td>{r.supplier || "—"}</td>
                      <td>{r.invoice_number || "—"}</td>
                      <td>{fmtDate(r.bill_date)}</td>
                      <td className="text-end">{fmtMoney(r.xero_amount)}</td>
                      <td className="text-end">{fmtMoney(r.myglobe_amount)}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          {pagination.pages > 1 && (
            <div className="card-footer d-flex justify-content-between align-items-center">
              <span className="text-muted">
                Page {pagination.page} of {pagination.pages} —{" "}
                {pagination.total.toLocaleString("en-GB")} rows
              </span>
              <div>
                <button
                  className="btn btn-outline-secondary btn-sm me-2"
                  disabled={page <= 1 || rowsLoading}
                  onClick={() => setPage(page - 1)}
                >
                  Previous
                </button>
                <button
                  className="btn btn-outline-secondary btn-sm"
                  disabled={page >= pagination.pages || rowsLoading}
                  onClick={() => setPage(page + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default XeroReconciliation;
